import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { createPatientWithAppointment, fetchAppointments, addAppointment, updateAppointmentData } from "../redux/slices/appointmentSlice";
import { fetchRooms, fetchAvailableRooms } from "../redux/slices/roomSlice";
import patientService from "../services/patientService";
import "../styles/Form.css";

function ScheduleAppointmentForm({ onSuccess, preSelectedDate, appointment, preSelectedPatientId }) {
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);
  const doctorId = authState.doctor?.doctorId;
  const allRooms = useSelector((state) => state.rooms.rooms);
  const availableRooms = useSelector((state) => state.rooms.availableRooms);
  const appointments = useSelector((state) => state.appointments.appointments);
  const reduxError = useSelector((state) => state.appointments.error);
  const isEditMode = !!appointment;

  // Helper function to format time from appointment object
  const formatTimeToInput = (timeString) => {
    if (!timeString) return "";
    // Handle both "HH:MM" and "HH:MM:SS" formats
    return timeString.substring(0, 5);
  };

  // Helper function to format date to YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper function to get current time in HH:MM format
  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    // Patient fields
    firstName: isEditMode ? appointment.patient?.first_name || "" : "",
    lastName: isEditMode ? appointment.patient?.last_name || "" : "",
    phoneNumber: isEditMode ? appointment.patient?.phone_number || "" : "",
    // Appointment fields
    doctorId: isEditMode ? appointment.doctor_id : (doctorId || ""),
    appointmentDate: isEditMode ? appointment.appointment_date : (preSelectedDate ? formatDate(preSelectedDate) : formatDate(new Date())),
    appointmentTime: isEditMode ? formatTimeToInput(appointment.appointment_time) : getCurrentTime(),
    appointmentDuration: isEditMode ? appointment.appointment_duration : 30,
    roomId: isEditMode ? appointment.room_id : "",
    status: isEditMode ? appointment.status : "scheduled",
    remarks: isEditMode ? (appointment.remarks || "") : "",
  });

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Patient search and autocomplete state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(isEditMode ? appointment.patient : null);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 300 });

  // Show error from Redux or local state
  useEffect(() => {
    if (reduxError) {
      setError(reduxError);
    }
  }, [reduxError]);

  // Update dropdown position when it shows
  useEffect(() => {
    if (showSearchDropdown && searchInputRef.current) {
      const rect = searchInputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [showSearchDropdown]);

  // Update dropdown position on window scroll
  useEffect(() => {
    if (showSearchDropdown) {
      const handleScroll = () => {
        if (searchInputRef.current) {
          const rect = searchInputRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      };

      window.addEventListener("scroll", handleScroll, true);
      return () => window.removeEventListener("scroll", handleScroll, true);
    }
  }, [showSearchDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showSearchDropdown &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target) &&
        !event.target.closest("#patient-search-dropdown-schedule")
      ) {
        setShowSearchDropdown(false);
      }
    };

    if (showSearchDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSearchDropdown]);

  // Fetch all rooms on mount
  useEffect(() => {
    dispatch(fetchRooms());
  }, [dispatch]);

  // Update doctorId when it changes
  useEffect(() => {
    if (doctorId) {
      setFormData((prev) => ({
        ...prev,
        doctorId: doctorId,
      }));
    }
  }, [doctorId]);

  // Fetch available rooms when date, time, duration, or doctorId changes
  useEffect(() => {
    if (
      formData.appointmentDate &&
      formData.appointmentTime &&
      doctorId
    ) {
      dispatch(
        fetchAvailableRooms({
          appointmentDate: formData.appointmentDate,
          appointmentTime: formData.appointmentTime,
          duration: parseInt(formData.appointmentDuration),
          doctorId: doctorId,
        })
      );
    }
  }, [
    formData.appointmentDate,
    formData.appointmentTime,
    formData.appointmentDuration,
    doctorId,
    dispatch,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle patient search with debounce
  const handlePatientSearch = (query) => {
    console.log("=== SEARCH INITIATED ===");
    console.log("Query:", query);
    console.log("Doctor ID:", doctorId);
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      console.log("Empty query, hiding dropdown");
      setSearchResults([]);
      setShowSearchDropdown(false);
      setIsSearching(false);
      return;
    }

    // Show dropdown immediately and start searching
    setShowSearchDropdown(true);
    setIsSearching(true);

    // Debounce the search
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        console.log("Calling searchPatients with:", query, "doctorId:", doctorId);
        const results = await patientService.searchPatients(query, doctorId);
        console.log("Search results:", results);
        setSearchResults(results || []);
      } catch (err) {
        console.error("Error searching patients:", err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  // Handle patient selection from dropdown
  const handleSelectPatient = (patient) => {
    console.log("Selected patient:", patient);
    setSelectedPatient(patient);
    setFormData((prev) => ({
      ...prev,
      firstName: patient.first_name,
      lastName: patient.last_name,
      phoneNumber: patient.phone_number,
    }));
    setSearchQuery("");
    setSearchResults([]);
    setShowSearchDropdown(false);
    setIsSearching(false);
  };

  // Clear patient selection
  const handleClearPatient = () => {
    setSelectedPatient(null);
    setSearchQuery("");
    setSearchResults([]);
    setShowSearchDropdown(false);
    setIsSearching(false);
    setFormData((prev) => ({
      ...prev,
      firstName: "",
      lastName: "",
      phoneNumber: "",
    }));
  };

  // Function to check for appointment clash
  const checkAppointmentClash = () => {
    const newApptTime = formData.appointmentTime;
    const newApptDate = formData.appointmentDate;
    const newRoomId = parseInt(formData.roomId);
    const newDuration = parseInt(formData.appointmentDuration);

    // Convert time string to minutes since midnight
    const [hours, mins] = newApptTime.split(":").map(Number);
    const newStartMinutes = hours * 60 + mins;
    const newEndMinutes = newStartMinutes + newDuration;

    // Check against existing appointments for the same room only
    for (const apt of appointments) {
      // Only check for same room, same date, and only SCHEDULED appointments
      if (
        apt.room_id === newRoomId &&
        apt.appointment_date === newApptDate &&
        apt.status === "scheduled"
      ) {
        const [existingHours, existingMins] = apt.appointment_time
          .split(":")
          .map(Number);
        const existingStartMinutes = existingHours * 60 + existingMins;
        const existingEndMinutes =
          existingStartMinutes + (apt.appointment_duration || 30);

        // Check for overlap
        if (
          newStartMinutes < existingEndMinutes &&
          newEndMinutes > existingStartMinutes
        ) {
          const existingEndHours = Math.floor(existingEndMinutes / 60);
          const existingEndMins = existingEndMinutes % 60;
          const existingEndTime = `${String(existingEndHours).padStart(2, "0")}:${String(existingEndMins).padStart(2, "0")}`;

          return `Appointment Clash! Room is already booked from ${apt.appointment_time} to ${existingEndTime} on this date. Please choose a different time.`;
        }
      }
    }

    return null; // No clash
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Validate appointment fields (always required)
    if (!formData.doctorId) {
      setError("Doctor ID is required");
      setIsLoading(false);
      return;
    }

    if (!formData.roomId) {
      setError("Please select a room");
      setIsLoading(false);
      return;
    }

    if (!formData.appointmentDate || !formData.appointmentTime) {
      setError("Please select appointment date and time");
      setIsLoading(false);
      return;
    }

    // Check for appointment clash
    const clashError = checkAppointmentClash();
    if (clashError) {
      setError(clashError);
      setIsLoading(false);
      return;
    }

    // Handle edit mode - update existing appointment
    if (isEditMode) {
      const submitData = {
        doctorId: parseInt(formData.doctorId),
        patientId: selectedPatient.patient_id,
        appointmentDate: formData.appointmentDate,
        appointmentTime: formData.appointmentTime,
        appointmentDuration: parseInt(formData.appointmentDuration),
        roomId: parseInt(formData.roomId),
        status: formData.status,
        remarks: formData.remarks,
      };

      dispatch(updateAppointmentData({
        id: appointment.appointment_id,
        appointmentData: submitData
      })).then((result) => {
        setIsLoading(false);
        if (result.type === updateAppointmentData.fulfilled.type) {
          dispatch(fetchAppointments(doctorId));
          
          setTimeout(() => {
            if (onSuccess) {
              onSuccess();
            }
          }, 1500);
        }
      });
      return;
    }

    // If patient is selected from search, create appointment with existing patient
    if (selectedPatient) {
      console.log("Creating appointment for existing patient:", selectedPatient.patient_id);
      const submitData = {
        doctorId: parseInt(formData.doctorId),
        patientId: selectedPatient.patient_id,
        appointmentDate: formData.appointmentDate,
        appointmentTime: formData.appointmentTime,
        appointmentDuration: parseInt(formData.appointmentDuration),
        roomId: parseInt(formData.roomId),
        status: formData.status,
        remarks: formData.remarks,
      };

      dispatch(addAppointment(submitData)).then((result) => {
        setIsLoading(false);
        if (result.type === addAppointment.fulfilled.type) {
          // Fetch updated appointments list
          dispatch(fetchAppointments(doctorId));
          
          // Close modal after a short delay
          setTimeout(() => {
            if (onSuccess) {
              onSuccess();
            }
          }, 1500);
        }
      });
      return;
    }

    // Otherwise, validate and create new patient with appointment
    if (!formData.firstName || !formData.firstName.trim()) {
      setError("Patient first name is required");
      setIsLoading(false);
      return;
    }

    if (!formData.lastName || !formData.lastName.trim()) {
      setError("Patient last name is required");
      setIsLoading(false);
      return;
    }

    if (!formData.phoneNumber || !formData.phoneNumber.trim()) {
      setError("Patient phone number is required");
      setIsLoading(false);
      return;
    }

    console.log("Creating new patient with appointment");
    const submitData = {
      // Patient data
      doctorId: parseInt(formData.doctorId),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      phoneNumber: formData.phoneNumber.trim(),
      // Appointment data
      appointmentDate: formData.appointmentDate,
      appointmentTime: formData.appointmentTime,
      appointmentDuration: parseInt(formData.appointmentDuration),
      roomId: parseInt(formData.roomId),
      status: formData.status,
      remarks: formData.remarks,
    };

    dispatch(createPatientWithAppointment(submitData)).then((result) => {
      setIsLoading(false);
      if (result.type === createPatientWithAppointment.fulfilled.type) {
        // Fetch updated appointments list
        dispatch(fetchAppointments(doctorId));
        
        // Close modal after a short delay
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
        }, 1500);
      }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="schedule-appointment-form">
      {error && <div style={{ color: "#d32f2f", marginBottom: "15px", padding: "10px", backgroundColor: "#ffebee", borderRadius: "4px" }}>{error}</div>}

      {/* Patient Information Section */}
      <div className="form-section" style={{ position: "relative", zIndex: 50 }}>
        <h4>Patient Information</h4>

        {selectedPatient ? (
          <div style={{ position: "relative", padding: "10px", backgroundColor: "#e8f5e9", borderRadius: "4px", marginBottom: "15px" }}>
            <button
              type="button"
              onClick={handleClearPatient}
              style={{
                position: "absolute",
                top: "8px",
                right: "8px",
                background: "none",
                border: "none",
                fontSize: "1rem",
                cursor: "pointer",
                color: "#999",
                padding: "0",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "color 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.color = "#d32f2f")}
              onMouseLeave={(e) => (e.target.style.color = "#999")}
              title="Remove patient"
            >
              ✕
            </button>
            <div style={{ marginBottom: "10px", paddingRight: "30px" }}>
              <strong>Selected Patient:</strong> {selectedPatient.first_name} {selectedPatient.last_name}
            </div>
            <div style={{ paddingRight: "30px" }}>
              <strong>Phone:</strong> {selectedPatient.phone_number || "N/A"}
            </div>
          </div>
        ) : (
          <>
            {/* Patient Search Input */}
            <div className="form-group" style={{ position: "relative", zIndex: 100 }}>
              <label>Search Existing Patient</label>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handlePatientSearch(e.target.value)}
                placeholder="Enter patient's first name..."
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "0.95rem",
                  boxSizing: "border-box",
                  position: "relative",
                  zIndex: 101,
                }}
              />
              
              {/* Search Results Dropdown - Rendered as Portal */}
              {showSearchDropdown &&
                ReactDOM.createPortal(
                  <div
                    style={{
                      position: "fixed",
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`,
                      width: `${dropdownPosition.width}px`,
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 10001,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                    id="patient-search-dropdown-schedule"
                  >
                    {searchResults.length > 0 ? (
                      searchResults.map((patient) => (
                        <div
                          key={patient.patient_id}
                          onClick={() => handleSelectPatient(patient)}
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #eee",
                            cursor: "pointer",
                            backgroundColor: "white",
                            transition: "backgroundColor 0.2s",
                          }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = "white")}
                        >
                          <div style={{ fontWeight: "500" }}>
                            {patient.first_name} {patient.last_name}
                          </div>
                          <div style={{ fontSize: "0.85rem", color: "#666" }}>
                            Phone: {patient.phone_number || "N/A"}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: "10px", color: "#999", textAlign: "center" }}>
                        {isSearching ? "Searching..." : (searchQuery ? "No patients found" : "Type to search patients")}
                      </div>
                    )}
                  </div>,
                  document.body
                )}
            </div>
          </>
        )}

        {!selectedPatient && (
          <>
            {/* Separator */}
            <div style={{  margin: "10px 0" }}>
              <p style={{ textAlign: "center", color: "#999", fontSize: "0.85rem", margin: "10px 0 0 0" }}>
                OR create a new patient
              </p>
            </div>

            {/* Manual Patient Fields */}
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Patient's first name"
                required
              />
            </div>

            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Patient's last name"
                required
              />
            </div>

            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="Patient's phone number"
                required
              />
            </div>
          </>
        )}
      </div>

      {/* Appointment Information Section */}
      <div className="form-section">
        <h4>Appointment Details</h4>

        <div className="form-group">
          <label>Appointment Date *</label>
          <input
            type="date"
            name="appointmentDate"
            value={formData.appointmentDate}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Appointment Time *</label>
          <input
            type="time"
            name="appointmentTime"
            value={formData.appointmentTime}
            onChange={handleChange}
            min="08:00"
            max="23:00"
            required
          />
        </div>

        <div className="form-group">
          <label>Duration (minutes) *</label>
          <input
            type="number"
            name="appointmentDuration"
            value={formData.appointmentDuration}
            onChange={handleChange}
            min="15"
            max="480"
            step="15"
            required
          />
        </div>

        <div className="form-group">
          <label>Select Room *</label>
          <select
            name="roomId"
            value={formData.roomId}
            onChange={handleChange}
            required
          >
            <option value="">-- Select a Room --</option>
            {availableRooms && availableRooms.length > 0 ? (
              <>
                {availableRooms.map((room) => (
                  <option key={room.room_id} value={room.room_id}>
                    {room.room_number} (Available)
                  </option>
                ))}
                {isEditMode && formData.roomId && !availableRooms.find(r => r.room_id === parseInt(formData.roomId)) && (
                  <option value={formData.roomId}>
                    {allRooms?.find(r => r.room_id === parseInt(formData.roomId))?.room_number} (Current)
                  </option>
                )}
              </>
            ) : formData.appointmentDate && formData.appointmentTime ? (
              <>
                <option disabled>No rooms available for this time</option>
                {isEditMode && formData.roomId && (
                  <option value={formData.roomId}>
                    {allRooms?.find(r => r.room_id === parseInt(formData.roomId))?.room_number} (Current)
                  </option>
                )}
              </>
            ) : (
              allRooms && allRooms.map((room) => (
                <option key={room.room_id} value={room.room_id}>
                  {room.room_number}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="form-group">
          <label>Status</label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            disabled={!isEditMode}
          >
            {isEditMode ? (
              <>
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="no_show">No Show</option>
              </>
            ) : (
              <option value="scheduled">Scheduled</option>
            )}
          </select>
        </div>

        <div className="form-group">
          <label>Remarks</label>
          <textarea
            name="remarks"
            value={formData.remarks}
            onChange={handleChange}
            placeholder="Add any additional notes..."
            rows="3"
          />
        </div>
      </div>

      <button
        type="submit"
        className="submit-btn"
        disabled={isLoading}
      >
        {isLoading ? (isEditMode ? "Updating..." : "Scheduling...") : (isEditMode ? "Update Appointment" : "Schedule Appointment")}
      </button>

      {error && (
        <div style={{
          color: "#d32f2f",
          marginBottom: "15px",
          padding: "10px",
          backgroundColor: "#ffebee",
          borderRadius: "4px",
          fontSize: "14px",
        }}>
          {error}
        </div>
      )}
    </form>
  );
}

export default ScheduleAppointmentForm;
